import { useState } from 'react';

import {
  Loader2,
  FileText,
  Hash,
  Link2,
  Copy,
  Check,
  Plus,
  ArrowRight,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../lib/mockData';
import { AppLayout } from '../components/AppLayout';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { send } from '../components';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../service/CoreFunctions';

const CreateInvoice = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();

  const [amount, setAmount] = useState('');
  const [txndesc, setTxnDesc] = useState('');
  const [merchantReference, setMerchantReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [createdInvoice, setCreatedInvoice] = useState(null);
  const [copied, setCopied] = useState(false);

  // Split payment states
  const [isSplit, setIsSplit] = useState(false);
  const [splitCount, setSplitCount] = useState('1');
  const [amountPerPerson, setAmountPerPerson] = useState('');

  const MIN_AMOUNT = Number(user?.invoiceAmountMin) || 500;
  const BASE_MAX_AMOUNT = Number(user?.invoiceAmountMax) || 5000000;
  const MAX_PEOPLE = 1000;

  const getEffectiveMaxAmount = () => {
    const count = isSplit ? (Number(splitCount) || 1) : 1;
    return BASE_MAX_AMOUNT * count;
  };

  const formatMoney = (value) => {
    if (typeof value !== 'string') value = String(value);
    const clean = value.replace(/[^0-9.]/g, "");
    if (!clean) return "";

    const [int, dec] = clean.split(".");
    const formattedInt = Number(int).toLocaleString("en-US");

    return dec !== undefined
      ? `${formattedInt}.${dec.substring(0, 2)}`
      : formattedInt;
  };

  const toNumber = (v) => Number(String(v || '').replace(/,/g, '')) || 0;

  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

  const getFeePercentNum = () => {
    const p = Number(feePercent);
    if (![0, 1, 2, 3, 4].includes(p)) return 0;
    return p;
  };

  const calcTotals = (base) => {
    const p = getFeePercentNum();
    const total = round2(base / (1 - p / 100));
    const fee = round2(total - base);
    return { p, fee, total };
  };

  const handleAmountChange = (raw) => {
    const formatted = formatMoney(raw);
    const base = toNumber(formatted);

    const { total } = calcTotals(base);

    // Upper bound guard (нийт дээр)
    const effectiveMax = getEffectiveMaxAmount();
    if (total > effectiveMax) {
      toast.error(`${t('amount')} ${effectiveMax.toLocaleString()}₮ ${t('max_allowed')}`);
      return;
    }

    setAmount(formatted);

    if (isSplit) {
      const numCount = Number(splitCount) || 1;
      const perPersonTotal = total / numCount;
      setAmountPerPerson(formatMoney(perPersonTotal.toFixed(2)));
    }
  };

  const handleAmountPerPersonChange = (raw) => {
    const formatted = formatMoney(raw);
    const basePer = toNumber(formatted);
    const numCount = Number(splitCount) || 1;

    const baseTotal = basePer * numCount;
    const { total } = calcTotals(baseTotal);

    const effectiveMax = getEffectiveMaxAmount();
    if (total > effectiveMax) {
      toast.error(`${t('amount')} ${effectiveMax.toLocaleString()}₮ ${t('max_allowed')}`);
      return;
    }

    setAmountPerPerson(formatted);
    setAmount(formatMoney(baseTotal.toFixed(2)));
  };

  const handleSplitCountChange = (value) => {
    let count = value.replace(/[^0-9]/g, "");
    if (count === "0") count = "1";
    if (Number(count) > MAX_PEOPLE) {
      toast.error(`${t('max_people')} ${MAX_PEOPLE}`);
      count = String(MAX_PEOPLE);
    }
    setSplitCount(count);

    const numCount = Number(count) || 1;
    if (isSplit) {
      const numericTotal = Number(amount.replace(/,/g, "")) || 0;
      const perPerson = numericTotal / numCount;
      setAmountPerPerson(formatMoney(perPerson.toFixed(2)));
    }
  };

  const toggleSplit = (enabled) => {
    setIsSplit(enabled);
    setSplitCount('1');
    setAmountPerPerson(amount);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1) base amount (хэрэглэгчийн оруулсан үндсэн дүн)
    const baseAmount = toNumber(amount); // amount string -> number

    // 2) fee + total
    const { p: feePercentNum, fee: feeAmount, total: totalAmount } = calcTotals(baseAmount);

    // 3) split count
    const count = isSplit ? (Number(splitCount.replace(/,/g, "")) || 1) : 1;

    // 4) Validation - MIN/MAX нь нийт дээр (нийт төлөх дүн)
    if (totalAmount < MIN_AMOUNT) {
      toast.error(`${t('min_amount')} ${MIN_AMOUNT.toLocaleString()}₮`);
      return;
    }

    const effectiveMax = getEffectiveMaxAmount();
    if (totalAmount > effectiveMax) {
      toast.error(`${t('max_amount')} ${effectiveMax.toLocaleString()}₮`);
      return;
    }

    // 5) per person total (split үед хүн бүрийн төлөх нийт дүн)
    const perPersonTotal = count > 0 ? (totalAmount / count) : totalAmount;

    setLoading(true);
    try {
      const res = await send("cu0900", {
        // ✅ backend-д хамгийн бага өөрчлөлт: amount_total дээр нийт дүн
        amount_total: totalAmount,

        // split логик хэвээр
        count_total: count,
        amount: Number(perPersonTotal.toFixed(2)), // хүн бүрийн төлөх дүн

        txndesc,
        merchant_ref: merchantReference,

        // (сонголтоор) backend optional байдлаар хадгалж болно
        fee_percent: feePercentNum,  // 0..4
        fee_amount: Number(feeAmount.toFixed(2)),
        base_amount: Number(baseAmount.toFixed(2)),
      });

      if (res?.status === 'error') {
        toast.error(res.message || t('error'));
        return;
      }

      setCreatedInvoice(res);
      toast.success(t('invoiceCreated'));
    } catch (err) {
      console.error(err);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };
  const handleCopyLink = () => {
    if (createdInvoice) {
      navigator.clipboard.writeText(createdInvoice.payment_link);
      setCopied(true);
      toast.success(t('copied'));
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCreateAnother = () => {
    setCreatedInvoice(null);
    setAmount('');
    setTxnDesc('');
    setMerchantReference('');
  };

  const [feePercent, setFeePercent] = useState('0'); // '0'...'4'

  if (createdInvoice) {
    return (
      <AppLayout>
        <div className="max-w-lg mx-auto space-y-8 animate-fade-up">
          <div className="text-center">
            <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-success" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              {t('invoiceCreated')}
            </h1>
            <p className="mt-2 text-muted-foreground">
              {t('sharePaymentLink')}
            </p>
          </div>

          <div className="card-elevated p-6 space-y-6">
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-1">{t('amount')}</p>
              <p className="sm:text-4xl font-bold text-foreground whitespace-normal text-2xl">
                {formatCurrency(createdInvoice.amount, i18n.language, user.currency)}
              </p>
            </div>

            <div className="bg-muted/50 rounded-xl p-4 text-center">
              <p className="text-sm text-muted-foreground">{createdInvoice.txndesc}</p>
            </div>

            {/* QR Code Placeholder */}
            {/* <div className="flex justify-center">
              <div className="w-48 h-48 bg-muted rounded-2xl flex items-center justify-center">
                <QrCode className="h-24 w-24 text-muted-foreground/30" />
              </div>
            </div> */}

            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('paymentLink')}</Label>
              <div className="flex gap-2">
                <div className="flex-1 bg-muted rounded-xl px-4 py-3 text-sm font-mono text-muted-foreground truncate">
                  {createdInvoice.payment_link}
                </div>
                <Button
                  onClick={handleCopyLink}
                  variant={copied ? 'success' : 'default'}
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleCreateAnother}
              variant="outline"
              size="lg"
              className="w-full sm:flex-1 h-auto min-h-[3rem]"
            >
              <Plus className="mr-2 h-4 w-4 shrink-0" />
              <span className="whitespace-normal text-center">
                {t('createAnother')}
              </span>
            </Button>

            <Button
              onClick={handleCopyLink}
              variant="gradient"
              size="lg"
              className="w-full sm:flex-1 h-auto min-h-[3rem]"
            >
              <span className="whitespace-normal text-center">
                {t('copyLink')}
              </span>
              <ArrowRight className="ml-2 h-4 w-4 shrink-0" />
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-lg mx-auto space-y-8 animate-fade-up">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">
            {t('createInvoice')}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {t('createPaymentRequest')}
          </p>
        </div>

        <div className="card-elevated p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="amount">{t('amount')}</Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold">₮</span>
                <Input
                  id="amount"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  className="pl-11 text-lg font-semibold"
                  required
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium">
                  {user.currency}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {MIN_AMOUNT.toLocaleString()}₮ – {getEffectiveMaxAmount().toLocaleString()}₮ {t('interval_amount')}
              </p>
            </div>
            <div className="space-y-2">
              <Label>{t('feePercent') || 'Гүйлгээний шимтгэл (%)'}</Label>

              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {['0', '1', '2', '3', '4'].map(p => (
                  <Button
                    key={p}
                    type="button"
                    variant={feePercent === p ? 'default' : 'outline'}
                    className="w-full justify-center"
                    onClick={() => setFeePercent(p)}
                  >
                    {p}%
                  </Button>
                ))}
              </div>

              <p className="text-xs text-muted-foreground">
                {t('feeNote') || 'Шимтгэлийг нэхэмжлэх дүн дээр нэмнэ.'}
              </p>
            </div>
            <div className="pt-2">
              <div
                onClick={() => toggleSplit(!isSplit)}
                className={cn(
                  "flex items-center justify-between p-4 rounded-xl border-2 transition-all cursor-pointer",
                  isSplit ? "border-primary bg-primary/5 shadow-sm" : "border-muted bg-transparent hover:border-muted-foreground/30"
                )}
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
                    isSplit ? "bg-primary text-white" : "bg-muted text-muted-foreground"
                  )}>
                    <Users className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{t('splitPayment')}</p>
                    <p className="text-xs text-muted-foreground">{t('divideAmountBetweenPeople') || 'Divide amount among multiple people'}</p>
                  </div>
                </div>
                <div className={cn(
                  "w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all",
                  isSplit ? "border-primary bg-primary" : "border-muted"
                )}>
                  {isSplit && <Check className="h-4 w-4 text-white" />}
                </div>
              </div>

              {isSplit && (
                <div className="mt-4 p-4 bg-muted/30 rounded-xl space-y-4 animate-fade-down">
                  <div className="grid lg:grid-cols-2 md:grid-cols-2 sm:grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="splitCount">{t('numPeople')}</Label>
                      <div className="relative">
                        <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="splitCount"
                          type="text"
                          inputMode="numeric"
                          value={splitCount}
                          onChange={(e) => handleSplitCountChange(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                    </div>


                    <div className="space-y-2">
                      <Label htmlFor="amountPerPerson">{t('amountPerPerson')}</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-xs">₮</span>
                        <Input
                          id="amountPerPerson"
                          type="text"
                          inputMode="decimal"
                          placeholder="0.00"
                          value={amountPerPerson}
                          onChange={(e) => handleAmountPerPersonChange(e.target.value)}
                          className="pl-7"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="description">{t('description')}</Label>
                <span className={cn(
                  "text-[10px] font-medium px-1.5 py-0.5 rounded border transition-colors",
                  txndesc.length >= 60
                    ? "text-destructive border-destructive/20 bg-destructive/5"
                    : "text-muted-foreground border-border/50 bg-muted/30"
                )}>
                  {txndesc.length} / 60
                </span>
              </div>
              <div className="relative">
                <FileText className="absolute left-4 top-3.5 h-4 w-4 text-muted-foreground" />
                <Textarea
                  id="description"
                  placeholder={t('whatIsPaymentFor')}
                  value={txndesc}
                  onChange={(e) => setTxnDesc(e.target.value)}
                  className="pl-11 min-h-[50px] resize-none"
                  maxLength={60}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reference">{t('merchantReference')}</Label>
              <div className="relative">
                <Hash className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="reference"
                  placeholder="REF-001"
                  value={merchantReference}
                  onChange={(e) => setMerchantReference(e.target.value)}
                  className="pl-11"
                />
              </div>
            </div>

            {amount && (() => {
              const base = toNumber(amount);
              const { p, fee, total } = calcTotals(base);

              return (
                <div className="bg-accent/50 rounded-xl p-4 animate-fade-up space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      {t('baseAmount') || 'Үндсэн'}
                    </span>
                    <span className="text-base font-semibold text-foreground text-right break-words">
                      {formatCurrency(base, i18n.language, user.currency)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      {t('fee') || 'Шимтгэл'} ({p}%)
                    </span>
                    <span className="text-base font-semibold text-foreground text-right break-words">
                      {formatCurrency(fee, i18n.language, user.currency)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-3 border-t pt-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      {t('total') || 'Нийт'}
                    </span>
                    <span className="text-lg font-bold text-foreground text-right break-words">
                      {formatCurrency(total, i18n.language, user.currency)}
                    </span>
                  </div>
                </div>
              );
            })()}

            <Button
              type="submit"
              variant="gradient"
              size="lg"
              className="w-full mt-6"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('loading')}
                </>
              ) : (
                <>
                  <Link2 className="mr-2 h-4 w-4" />
                  {t('createInvoice')}
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </AppLayout>
  );
};

export default CreateInvoice;
